import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, Loader2 } from 'lucide-react';

// AuthCallback — one-time exchange of the Emergent Auth session_id (found
// in the URL fragment) for our own session cookie + JWT. This screen is
// rendered by AppRouter whenever `useLocation().hash` contains
// `session_id=`; the AuthProvider intentionally short-circuits its
// /auth/me check while this hash is present so this screen owns the
// exchange and no 401 races happen.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
export default function AuthCallback() {
  const { loginWithGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Idempotency guard — under React StrictMode, effects run twice. Only
    // exchange the session_id once, even if the component mounts twice.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError('No session_id in the URL. Try signing in again.');
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        await loginWithGoogleSession(sessionId);
        // Scrub the session_id from the URL immediately so it doesn't leak
        // in the address bar, back-button history, or referer headers.
        window.history.replaceState({}, document.title, '/');
        navigate('/', { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const raw = err?.response?.data?.detail;
        let detail;
        if ([502, 503, 504, 520, 521, 522, 523, 524].includes(status)) {
          // Gateway / origin errors — the browser reached our domain but the
          // origin backend did not return a valid response. This is almost
          // always an infra/deployment issue rather than our code. Give a
          // clean, honest explanation instead of surfacing Cloudflare HTML.
          detail = `Our sign-in service didn't respond (gateway ${status}). This usually means the backend is redeploying or the Cloudflare↔origin route is unhealthy. Wait a minute and try again; if it keeps happening, contact Emergent Support with the ray ID from the browser network tab.`;
        } else if (typeof raw === 'string' && raw.length) {
          detail = raw;
        } else {
          detail = err?.message || 'Sign-in failed. Try again.';
        }
        setError(detail);
      }
    })();
  }, [location.hash, loginWithGoogleSession, navigate]);

  return (
    <div className="min-h-screen bg-indie-navy text-indie-cream flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {error ? (
          <div data-testid="auth-callback-error">
            <AlertTriangle className="mx-auto text-indie-yellow" size={32} />
            <h1 className="mt-4 text-2xl font-semibold text-white">Sign-in didn&#39;t complete</h1>
            <p className="mt-3 text-sm text-indie-cream/70">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/signin', { replace: true })}
              data-testid="auth-callback-back-btn"
              className="mt-6 inline-flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold px-4 py-2 hover:bg-indie-yellow/85"
            >
              Back to sign-in
            </button>
          </div>
        ) : (
          <div data-testid="auth-callback-loading">
            <Loader2 className="mx-auto animate-spin text-indie-yellow" size={32} />
            <h1 className="mt-4 text-2xl font-semibold text-white">Finishing sign-in…</h1>
            <p className="mt-3 text-sm text-indie-cream/70">Exchanging your Google session with the Command Center.</p>
          </div>
        )}
      </div>
    </div>
  );
}
